import PropTypes from "prop-types";
import React, { useState } from "react";
import { formatMoney } from "../helper";
import {
  computeDiff,
  countRootInvoices,
  formatQuantity,
  onlyDigits,
  rowErrors,
  sumRows,
} from "../statementHelper";

// Chỉ format phần hiển thị khi ô không được gõ. Giữ giá trị thô trong state
// nên con trỏ không nhảy lúc gõ, mà dữ liệu nạp từ API cũng hiện đúng định dạng.
const formatForDisplay = (kind, value) => {
  if (value === null || value === undefined || value === "") return "";
  return kind === "money" ? formatMoney(value) : formatQuantity(value);
};

// Ba nhóm cột ba tông màu để mắt bám được đang đọc phần nào
const GROUP_STYLE = {
  before: { background: "rgba(116, 120, 141, 0.14)", color: "#495057" },
  after: { background: "rgba(85, 110, 230, 0.16)", color: "#3b5bdb" },
  diff: { background: "rgba(241, 180, 76, 0.20)", color: "#a0730f" },
};

const SUB_STYLE = {
  before: { background: "rgba(116, 120, 141, 0.06)" },
  after: { background: "rgba(85, 110, 230, 0.07)" },
  diff: { background: "rgba(241, 180, 76, 0.09)" },
};

const COL_BEFORE = [
  {
    key: "serial",
    text: "Ký hiệu hóa đơn",
    no: 2,
    noLabel: "(2)(3)",
    serial: true,
  },
  { key: "ttchung_shdon", text: "Số hóa đơn", no: 4 },
  { key: "thhdvu", text: "Tên hàng hóa, dịch vụ", no: 5, wide: true },
  { key: "sluong", text: "Số lượng", no: 6, number: true },
  { key: "dgia", text: "Đơn giá", no: 7, money: true },
  { key: "thtien", text: "Thành tiền", no: 8, money: true },
  { key: "tsuat", text: "Thuế suất", no: 9 },
  { key: "tthue", text: "Thuế GTGT", no: 10, money: true },
  { key: "tong", text: "Tổng tiền", no: 11, money: true },
];

// Người dùng nhập 4 cột; 3 cột định danh hóa đơn và 3 cột tiền đều khóa lại
const COL_AFTER = [
  {
    key: "serial",
    text: "Ký hiệu hóa đơn",
    no: 14,
    noLabel: "(14)(15)",
    serial: true,
  },
  { key: "ttchung_shdon", text: "Số hóa đơn", no: 16 },
  {
    key: "thhdvu",
    text: "Tên hàng hóa, dịch vụ",
    no: 17,
    edit: true,
    wide: true,
  },
  {
    key: "sluong",
    text: "Số lượng",
    no: 18,
    edit: true,
    number: true,
    blurFormat: "quantity",
    minWidth: 90,
    digitsOnly: true,
  },
  {
    key: "dgia",
    text: "Đơn giá",
    no: 19,
    edit: true,
    number: true,
    blurFormat: "money",
    minWidth: 150,
    digitsOnly: true,
  },
  { key: "thtien", text: "Thành tiền", no: 20, money: true },
  { key: "tsuat", text: "Thuế suất", no: 21, edit: true, minWidth: 100 },
  { key: "tthue", text: "Thuế GTGT", no: 22, money: true },
  { key: "tong", text: "Tổng tiền", no: 23, money: true },
  { key: "khac", text: "Khác", no: 24 },
];

const ID_COLUMNS = ["serial", "ttchung_shdon"];

// Response để các trường định danh null ở từng dòng, thông tin thật nằm ở
// cấp bảng kê (data.invoice)
const ID_FALLBACK = { ttchung_shdon: "shdon" };

// Ký hiệu đầy đủ = ký hiệu mẫu số + ký hiệu, ví dụ "2" + "C23TBG" -> "2C23TBG"
const buildSerial = (source, fallback) => {
  const khmshdon = source?.ttchung_khmshdon || fallback?.khmshdon || "";
  const khhdon = source?.ttchung_khhdon || fallback?.khhdon || "";
  return `${khmshdon}${khhdon}`;
};

const headerCell = (col, group) => (
  <th
    key={col.no}
    className="text-nowrap align-middle"
    style={SUB_STYLE[group]}
  >
    <div>{col.text}</div>
    <div className="text-muted fw-normal">{col.noLabel || `(${col.no})`}</div>
  </th>
);

// 0 để xám, tăng để cam đậm, giảm để đỏ — nhìn là biết dòng nào đổi
const DiffCell = ({ value }) => {
  if (value === null || value === undefined) {
    return <span className="text-muted">–</span>;
  }
  if (value === 0) return <span className="text-muted">0</span>;
  return (
    <span
      className={value > 0 ? "text-warning fw-bold" : "text-danger fw-bold"}
    >
      {value > 0 ? "+" : "-"}
      {formatMoney(Math.abs(value))}
    </span>
  );
};

DiffCell.propTypes = { value: PropTypes.number };
DiffCell.defaultProps = { value: null };

const StatementRowsTable = props => {
  const { rows, onChangeRow, invoice, readOnly } = props;

  const [focusedCell, setFocusedCell] = useState(null);

  const totals = sumRows(rows);

  return (
    <div className="table-responsive">
      <table className="table table-bordered align-middle font-size-12 mb-0">
        <thead className="table-light text-center">
          <tr>
            {/* Ô trống nằm trên STT, tô cùng màu dải tiêu đề nhóm để hàng
                trên liền một mạch. STT tách xuống hàng dưới (thay vì gộp 2
                dòng) để mỗi hàng ăn đúng màu của hàng đó. */}
            <th style={GROUP_STYLE.before} />
            <th colSpan={COL_BEFORE.length} style={GROUP_STYLE.before}>
              THÔNG TIN TRƯỚC ĐIỀU CHỈNH/ THAY THẾ
            </th>
            <th colSpan={COL_AFTER.length} style={GROUP_STYLE.after}>
              THÔNG TIN SAU ĐIỀU CHỈNH/ THAY THẾ (*)
            </th>
            <th colSpan="4" style={GROUP_STYLE.diff}>
              CHÊNH LỆCH (**)
            </th>
          </tr>
          <tr>
            <th
              className="text-center align-bottom"
              style={{ width: 56, ...SUB_STYLE.before }}
            >
              <div>STT</div>
              <div className="text-muted fw-normal">(1)</div>
            </th>
            {COL_BEFORE.map(col => headerCell(col, "before"))}
            {COL_AFTER.map(col => headerCell(col, "after"))}
            {headerCell({ no: 25, text: "Tổng tiền trước thuế" }, "diff")}
            {headerCell({ no: 26, text: "Thuế GTGT" }, "diff")}
            {headerCell({ no: 27, text: "Tổng tiền thanh toán" }, "diff")}
            {headerCell({ no: 28, text: "Khác" }, "diff")}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => {
            const errors = rowErrors(row);
            const diff = computeDiff(row.before, row.after);
            const after = row.after || {};

            return (
              <tr key={row.detail_id}>
                <td className="text-center">{row.stt ?? index + 1}</td>

                {COL_BEFORE.map(col => (
                  <td
                    key={col.no}
                    className={
                      col.money || col.number ? "text-end text-nowrap" : ""
                    }
                    style={col.wide ? { minWidth: 260 } : undefined}
                  >
                    {col.serial
                      ? buildSerial(row.before)
                      : col.money
                      ? formatMoney(row.before?.[col.key])
                      : col.number
                      ? formatQuantity(row.before?.[col.key])
                      : row.before?.[col.key] || ""}
                  </td>
                ))}

                {COL_AFTER.map(col => {
                  if (col.key === "khac") {
                    return (
                      <td key={col.no} className="text-center text-muted">
                        –
                      </td>
                    );
                  }
                  // 3 cột định danh hóa đơn điều chỉnh: chỉ xem, lấy theo
                  // hóa đơn của bảng kê khi dòng chưa có giá trị riêng
                  if (ID_COLUMNS.includes(col.key)) {
                    return (
                      <td key={col.no} className="text-nowrap">
                        {col.serial
                          ? buildSerial(after, invoice)
                          : after[col.key] ||
                            invoice?.[ID_FALLBACK[col.key]] ||
                            ""}
                      </td>
                    );
                  }
                  if (!col.edit) {
                    return (
                      <td key={col.no} className="text-end text-nowrap">
                        {formatMoney(after[col.key])}
                      </td>
                    );
                  }
                  const invalid = errors[col.key];
                  const cellId = `${index}:${col.key}`;

                  // Bảng kê không còn sửa được thì hiện chữ thay cho ô nhập
                  if (readOnly) {
                    return (
                      <td
                        key={col.no}
                        className={col.number ? "text-end text-nowrap" : ""}
                        style={{
                          minWidth:
                            col.minWidth || (col.wide ? 260 : undefined),
                        }}
                      >
                        {col.blurFormat
                          ? formatForDisplay(col.blurFormat, after[col.key])
                          : after[col.key] || ""}
                      </td>
                    );
                  }
                  return (
                    <td
                      key={col.no}
                      style={{
                        minWidth: col.minWidth || (col.wide ? 260 : undefined),
                      }}
                    >
                      <input
                        type="text"
                        className={`form-control form-control-sm ${
                          col.number ? "text-end" : ""
                        } ${invalid ? "border-danger text-danger" : ""}`}
                        placeholder={invalid || ""}
                        value={
                          col.blurFormat && focusedCell !== cellId
                            ? formatForDisplay(col.blurFormat, after[col.key])
                            : after[col.key] ?? ""
                        }
                        onFocus={() => setFocusedCell(cellId)}
                        onBlur={() => setFocusedCell(null)}
                        inputMode={col.digitsOnly ? "numeric" : undefined}
                        onChange={event =>
                          onChangeRow(
                            index,
                            col.key,
                            col.digitsOnly
                              ? onlyDigits(event.target.value)
                              : event.target.value
                          )
                        }
                      />
                    </td>
                  );
                })}

                <td className="text-end text-nowrap">
                  <DiffCell value={diff.net} />
                </td>
                <td className="text-end text-nowrap">
                  <DiffCell value={diff.vat} />
                </td>
                <td className="text-end text-nowrap">
                  <DiffCell value={diff.pay} />
                </td>
                <td className="text-center text-muted">–</td>
              </tr>
            );
          })}
        </tbody>

        <tfoot className="table-light">
          <tr>
            <td colSpan={1 + COL_BEFORE.length} className="fw-bold">
              Tổng cộng {countRootInvoices(rows)} hóa đơn
            </td>
            <td colSpan={COL_AFTER.length - 2} />
            <td className="text-end text-nowrap fw-bold">
              {formatMoney(totals.afterTong)}
            </td>
            <td />
            <td className="text-end text-nowrap">
              <DiffCell value={totals.diffNet} />
            </td>
            <td className="text-end text-nowrap">
              <DiffCell value={totals.diffVat} />
            </td>
            <td className="text-end text-nowrap">
              <DiffCell value={totals.diffPay} />
            </td>
            <td className="text-center text-muted">–</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

StatementRowsTable.propTypes = {
  rows: PropTypes.array.isRequired,
  readOnly: PropTypes.bool,
  onChangeRow: PropTypes.func.isRequired,
  invoice: PropTypes.object,
};

StatementRowsTable.defaultProps = {
  invoice: null,
  readOnly: false,
};

export default StatementRowsTable;
